/* eslint-disable no-useless-escape */
import axios from "axios";
import ILocation, {
  ICarrier,
  IOccupant,
  IPinExtension,
  IPinGroup,
} from "../types/ILocation";
import IIndication from "../types/IIndication";
import IIndicationHistory from "../types/IIndicationHistory";
import IProduct from "../types/IProduct";
import { sortByField } from "./Sort";
import BaseService from "./base.service";
import {
  ICreateFavoriteParams,
  ICreateFavoriteResponse,
  ICreatePeriodInfoParams,
  ICreatePeriodPriceParams,
  ICreatePeriodPriceResponse,
  IDeleteFavoriteParams,
  IEmptyCrudResponse,
  IFavoriteOriginResponse,
  IFavoriteOriginOption,
  IGetFavoriteOriginsParams,
  IMonthSplitParams,
  IMonthSplitResponse,
  IReorderFavoritesParams,
  IUpdatePeriodPriceParams,
  IUpdatePeriodPriceResponse,
} from "../types/IFavoriteOrigins";
import {
  IGetQuoteParams,
  IGetQuotesParams,
  IGetQuotesResponse,
  ISurroundingLocationsParams,
} from "../types/IQuotes";
import {
  ICarrierStationCreateParams,
  ICarrierStationDeleteParams,
  IUpdateLocationModesParams,
  IUpdateLocationModes2Params,
  IRailStationUpdateParams,
  ISCRSRecord,
} from "../panels/mobile/railStationLookupPanel/IRailStationLookup";
import { IMileageRequestStatusParams } from "../types/IMileageRequest";
import getQuotesByDestination2Data from "../mockData/GetQuoteByDestination2Data.json";
import getFavoritesByMarketerData from "../mockData/GetFavoritesByMarketerData.json";
import createFavoriteSampleData from "../mockData/CreateFavorite.sample.json";
import deleteFavoriteSampleData from "../mockData/DeleteFavorite.sample.json";
import reorderFavoritesSampleData from "../mockData/ReorderFavorites.sample.json";
import createPeriodPriceSampleData from "../mockData/CreatePeriodPrice.sample.json";
import updatePeriodPriceSampleData from "../mockData/UpdatePeriodPrice.sample.json";
import createPeriodInfoSampleData from "../mockData/CreatePeriodInfo.sample.json";
import splitMonthSampleData from "../mockData/SplitMonth.sample.json";
import unsplitMonthSampleData from "../mockData/UnsplitMonth.sample.json";
import locationDetails2SampleData from "../mockData/LocationDetails2.sample.json";
import multiLocationDetails2SampleData from "../mockData/MultiLocationDetails2.sample.json";

interface IMockEnvelope<TRequest, TResponse> {
  query: string;
  request: TRequest;
  response: TResponse;
}

interface ICreateLocationParams {
  id: string;
  name: string;
  owner: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  coordinates: [number, number];
  uom: string;
  threeLegsAllowed?: boolean | null;
  equipmentTypes?: string[] | null;
  modes: { id: string; inbound: boolean; outbound: boolean }[];
  railStations: {
    scrsId?: string;
    stationState?: string;
    carriers: string[];
  }[];
  occupants?: {
    id?: string;
    name: string;
    products: { product: string; quantity: number }[];
    types: ("Customer" | "Competitor" | "Koch")[];
    states: string[];
  }[];
}

export default class NitroService extends BaseService {
  private static authenticatedUserId: string | undefined;

  public static setAuthenticatedUserId(userId: string | undefined): void {
    this.authenticatedUserId = userId;
  }

  private static readonly useMockGetQuotesByDestination2 = false;
  private static readonly useMockGetFavoritesByMarketer = false;
  private static readonly useMockCreateFavorite = false;
  private static readonly useMockDeleteFavorite = false;
  private static readonly useMockReorderFavorites = false;
  private static readonly useMockCreatePeriodPrice = false;
  private static readonly useMockUpdatePeriodPrice = false;
  private static readonly useMockCreatePeriodInfo = false;
  private static readonly useMockSplitMonth = false;
  private static readonly useMockUnsplitMonth = false;
  private static readonly useMockLocationDetails2 = false;
  private static readonly useMockMultiLocationDetails2 = false;

  public static async getQuotes(
    params: IGetQuoteParams,
  ): Promise<IGetQuotesResponse[]> {
    const result = await this.proxy("GetQuotesByOD2", { ...params, userId: this.authenticatedUserId }, 1, 20);
    return result;
  }

  public static async getSCRSRailData(params: {
    cityState: string;
    stationState: string;
    customerName: string;
    address: string;
  }): Promise<ISCRSRecord[]> {
    const response = await this.proxy("SCRS", params, 1, 20);
    return response || [];
  }

  public static async updateRailTeamNotified(
    freightId: number,
    railTeamNotified: boolean = true,
  ) {
    const response = await this.post("UpdateRailTeamNotified", {
      traticsRateId: freightId,
      railTeamNotified,
    });
    return response?.data || [];
  }

  public static async getCarriers(): Promise<{ carrier: string }[]> {
    const response = await this.proxy("Carriers", {}, 1, 20);
    return response || [];
  }

  public static async createCarrierForStation(
    params: ICarrierStationCreateParams,
  ): Promise<any> {
    const response = await this.post("CarrierStationCreate", params);
    return response.data || 0;
  }

  public static async deleteCarrierForStation(
    params: ICarrierStationDeleteParams,
  ): Promise<any[]> {
    const response = await this.post("CarrierStationDelete", params);
    return response || [];
  }

  public static async updateRailStation(
    params: IRailStationUpdateParams,
  ): Promise<any[]> {
    const response = await this.post("UpdateRailStationAtLocation", params);
    return response || [];
  }

  public static async updateLocationModes2(
    params: IUpdateLocationModes2Params,
  ): Promise<any[]> {
    const response = await this.post("UpdateModesAtLocation2", params);
    return response || [];
  }

  public static async post(cypherName: string, body: any): Promise<any> {
    try {
      const updatedBody = Array.isArray(body) ? body : { ...(body || {}) };

      if (!Array.isArray(updatedBody)) {
        Object.keys(updatedBody).forEach((key) => {
          // keep explicit null values (e.g. price) since the backend expects the key to be present
          if (updatedBody[key] === undefined) {
            delete updatedBody[key];
          }
        });
      }

      const result = await axios({
        url: `${this.url}/proxy/${cypherName}`,
        method: "POST",
        data: updatedBody,
      });

      return result;
    } catch (e) {
      const err = e as any;
      console.error("API Error", err?.response);

      if (err?.response?.status === 0 || err?.response?.status === 401) {
        throw new Error("Permissions denied");
      }

      if (err.response.data?.errorMessage) {
        throw new Error(err.response.data.errorMessage);
      }

      throw e;
    }
  }

  public static async getQuotesByDestination(
    params: IGetQuotesParams,
    skip: number = 0,
    limit: number = 0,
  ): Promise<IGetQuotesResponse[]> {
    try {
      if (this.useMockGetQuotesByDestination2) {
        return getQuotesByDestination2Data as unknown as IGetQuotesResponse[];
      }

      const results = await this.proxy(
        "GetQuotesByDestination2",
        { ...params, userId: this.authenticatedUserId },
        skip,
        limit,
      );
      return results || [];
    } catch (error) {
      console.error(
        "Error in getDestinationsOrigins (GetQuotesByDestination2):",
        error,
      );
      throw error;
    }
  }

  public static async saveDlvdOptions(params: {
    marketer: string;
    truckDlvdOption: "px" | "alt";
    railDlvdOption: "px" | "alt";
  }): Promise<any> {
    const response = await this.post("SaveDlvdOptions", params);
    return response?.data || response;
  }

  public static async getSurroundingLocations(
    params: ISurroundingLocationsParams,
    skip: number,
    limit: number,
  ): Promise<IGetQuotesResponse[]> {
    try {
      const results = await this.proxy(
        "Surroundings2",
        { ...params, userId: this.authenticatedUserId },
        skip,
        limit,
      );
      return results ?? [];
    } catch (error) {
      console.error(
        "Error in getSurroundingLocations (CompAnalysisQMD2):",
        error,
      );
      throw error;
    }
  }

  public static async getPins(
    params: {
      locationId?: string;
      locationName?: string;
      modeId?: string | number;
      partyId?: string | number;
      partyTypes: string;
      occupantType?: string | number;
      logisticsProductGroupName?: string;
      cityStateId?: string | number;
      stateOrProvinceId?: string | number;
      shipDate?: string | number;
    },
    skip: number,
    limit: number,
  ): Promise<IPinExtension[]> {
    const groups = await this.proxy(
      "Location",
      {
        id: params.locationId || "",
        location_name: params.locationName || "",
        mode_id: params.modeId || "",
        party_id: params.partyId || "",
        occupant_type: params.occupantType || "",
        logisticsproductgroup_name: params.logisticsProductGroupName || "",
        citystate_id: params.cityStateId || "",
        stateorprovince_id: params.stateOrProvinceId || "",
        ship_date: params.shipDate || "",
        partyTypes: params.partyTypes,
      },
      skip,
      limit,
    );
    const flattenedLocations: IPinExtension[] = [];

    groups.forEach((group: IPinGroup) => {
      group.pins.forEach((pin) => {
        flattenedLocations.push({ ...pin, pinType: group.pinType });
      });
    });

    return flattenedLocations;
  }

  public static async getPartiesLike(value: string, marketer?: string) {
    const result = await this.proxy(
      "PartiesLike",
      {
        party: value,
        marketer: marketer || "",
      },
      1,
      20,
    );

    if (!result || result.length === 0) {
      return [];
    }

    return result;
  }

  public static async getLocationExpanded(filter: string) {
    const result = await this.proxy(
      "LocationExpanded",
      {
        filter,
      },
      1,
      1,
    );

    return result;
  }

  public static async getZipCodes(zipCode: string) {
    const result = await this.proxy("ZipCodes", { zipCode }, 1, 20);

    return result.map((r) => r.zipCode);
  }

  public static async getZipCodeDetails(zipCode: string) {
    const result = await this.proxy("ZipCodeDetails", { zipCode }, 1, 20);

    return result;
  }

  public static async getCityStateFreightZip(
    cityState: string,
  ): Promise<string | undefined> {
    if (!cityState) return undefined;

    const result = await this.proxy(
      "CityStateFreightZip",
      { cityState },
      1,
      20,
    );

    return result?.[0]?.zip;
  }

  public static async getProducts(): Promise<IProduct[]> {
    const result: IProduct[] = await this.proxy("Product", {}, 1, 20);

    return result || [];
  }

  public static async getUOMs() {
    const result = await this.proxy("UOMs", {}, 1, 20);

    return result;
  }

  public static async getStates() {
    const result = await this.proxy("State", {}, 1, 20);

    return result;
  }

  public static async getStateCoordinates(stateName: string) {
    const result = await this.proxy("StateCoordinates", { stateName }, 1, 20);

    return result as {
      xMin: number;
      xMax: number;
      yMin: number;
      yMax: number;
    }[];
  }

  public static async getCityStates(cityStateName: string, stateName?: string) {
    const params: { [key: string]: string } = {
      city: cityStateName,
    };

    if (stateName) {
      params.state = stateName;
    }

    const result = await this.proxy("CityStateLike", params, 1, 20);

    return result;
  }

  public static async getModes() {
    const result = await this.proxy("Mode", {}, 1, 20);
    return result;
  }

  public static async getLocationNames(locationName: string) {
    const result = await this.proxy(
      "LocationNameLike",
      {
        location_name: locationName,
      },
      1,
      20,
    );

    return result;
  }

  public static async mapEndpoint(): Promise<{ apiKey: string }> {
    const response: any = await this.get(`/map`);

    return response?.data;
  }

  public static async createLocation(
    params: ICreateLocationParams,
  ): Promise<any[]> {
    const response: any = await this.post(`LocationCreate`, params);

    return response?.data || [];
  }

  public static async getLocationDetails2(id: string) {
    if (!id) return undefined;

    if (this.useMockLocationDetails2) {
      const result = locationDetails2SampleData as unknown as any[];
      return result.length ? this.processLocationRecord2(result[0]) : undefined;
    }

    const result = await this.proxy(
      "LocationDetails2",
      {
        id,
      },
      1,
      20,
    );

    if (!result || result.length === 0) {
      return undefined;
    }

    return this.processLocationRecord2(result[0]);
  }

  public static async getMultiLocationDetails2(params: { ids: string[] }) {
    if (!params?.ids?.length) return undefined;

    if (this.useMockMultiLocationDetails2) {
      const result = multiLocationDetails2SampleData as unknown as any[];
      return result.map((record) => this.processLocationRecord2(record));
    }

    const IDS_PER_REQUEST = 150;
    const PAGE_SIZE = 100;
    const pagePromises = [];

    for (let i = 0; i < params.ids.length; i += IDS_PER_REQUEST) {
      const batchIds = params.ids.slice(i, i + IDS_PER_REQUEST);
      const batchParams = { ...params, ids: batchIds };

      pagePromises.push(
        this.proxy("MultiLocationDetails2", batchParams, 1, PAGE_SIZE),
      );
    }

    try {
      const results = await Promise.all(pagePromises);
      const combinedRecords = results
        .flat()
        .filter((record) => record !== null && record !== undefined);

      if (combinedRecords.length === 0) {
        return undefined;
      }

      return combinedRecords.map((record: any) =>
        this.processLocationRecord2(record),
      );
    } catch (error) {
      console.error("Error fetching multi-location details 2 pages", {
        ids: params.ids,
        error,
      });

      return undefined;
    }
  }

  // TODO: remove when railStations is guaranteed to always be an array in the data source
  private static processLocationRecord2(record: ILocation): ILocation {
    const railStations = record.railStations ? record.railStations : [];
    const [primaryStation] = railStations;

    return {
      ...record,
      railStations,
      stationState: primaryStation?.stationState,
      scrsId: primaryStation?.scrsId,
      carriers: primaryStation?.carriers || [],
    };
  }

  public static async getFavoritesByMarketer(
    params: IGetFavoriteOriginsParams,
    skip: number = 1,
    limit: number = 20,
  ): Promise<IFavoriteOriginResponse[]> {
    try {
      if (this.useMockGetFavoritesByMarketer) {
        return getFavoritesByMarketerData as unknown as IFavoriteOriginResponse[];
      }

      const results = (await this.proxy(
        "GetFavoritesByMarketer",
        { ...params, userId: this.authenticatedUserId },
        skip,
        limit,
      )) as IFavoriteOriginResponse[];
      return results || [];
    } catch (error) {
      console.error("Error in getFavoritesByMarketer:", error);
      throw error;
    }
  }

  public static async createFavorite(
    params: ICreateFavoriteParams,
  ): Promise<ICreateFavoriteResponse[]> {
    try {
      if (this.useMockCreateFavorite) {
        return (
          createFavoriteSampleData as IMockEnvelope<
            ICreateFavoriteParams,
            ICreateFavoriteResponse[]
          >
        ).response;
      }

      const response = await this.post("CreateFavorite", {
        ...params,
        userId: this.authenticatedUserId,
      });
      return response?.data || [];
    } catch (error) {
      console.error("Error in createFavorite:", error);
      throw error;
    }
  }

  public static async deleteFavorite(
    params: IDeleteFavoriteParams,
  ): Promise<IEmptyCrudResponse[]> {
    try {
      if (this.useMockDeleteFavorite) {
        return (
          deleteFavoriteSampleData as IMockEnvelope<
            IDeleteFavoriteParams,
            IEmptyCrudResponse[]
          >
        ).response;
      }

      const response = await this.post("DeleteFavorite", params);
      return response?.data || [];
    } catch (error) {
      console.error("Error in deleteFavorite:", error);
      throw error;
    }
  }

  public static async reorderFavorites(
    params: IReorderFavoritesParams,
  ): Promise<IEmptyCrudResponse[]> {
    try {
      if (this.useMockReorderFavorites) {
        return (
          reorderFavoritesSampleData as IMockEnvelope<
            IReorderFavoritesParams,
            IEmptyCrudResponse[]
          >
        ).response;
      }

      const response = await this.post("ReorderFavorites", params);
      return response?.data || [];
    } catch (error) {
      console.error("Error in reorderFavorites:", error);
      throw error;
    }
  }

  public static async createPeriodPrice(
    params: ICreatePeriodPriceParams,
  ): Promise<ICreatePeriodPriceResponse[]> {
    try {
      if (this.useMockCreatePeriodPrice) {
        return (
          createPeriodPriceSampleData as IMockEnvelope<
            ICreatePeriodPriceParams,
            ICreatePeriodPriceResponse[]
          >
        ).response;
      }

      const response = await this.post("CreatePeriodPrice", params);
      return response?.data || [];
    } catch (error) {
      console.error("Error in createPeriodPrice:", error);
      throw error;
    }
  }

  public static async updatePeriodPrice(
    params: IUpdatePeriodPriceParams,
  ): Promise<IUpdatePeriodPriceResponse[]> {
    try {
      if (this.useMockUpdatePeriodPrice) {
        return (
          updatePeriodPriceSampleData as IMockEnvelope<
            IUpdatePeriodPriceParams,
            IUpdatePeriodPriceResponse[]
          >
        ).response;
      }

      const response = await this.post("UpdatePeriodPrice", params);
      return response?.data || [];
    } catch (error) {
      console.error("Error in updatePeriodPrice:", error);
      throw error;
    }
  }

  public static async createPeriodInfo(
    params: ICreatePeriodInfoParams,
  ): Promise<IEmptyCrudResponse[]> {
    try {
      if (this.useMockCreatePeriodInfo) {
        return (
          createPeriodInfoSampleData as IMockEnvelope<
            ICreatePeriodInfoParams,
            IEmptyCrudResponse[]
          >
        ).response;
      }

      const response = await this.post("CreatePeriodInfo", params);
      return response?.data || [];
    } catch (error) {
      console.error("Error in createPeriodInfo:", error);
      throw error;
    }
  }

  public static async splitMonth(
    params: IMonthSplitParams,
  ): Promise<IMonthSplitResponse[]> {
    try {
      if (this.useMockSplitMonth) {
        return (
          splitMonthSampleData as IMockEnvelope<
            IMonthSplitParams,
            IMonthSplitResponse[]
          >
        ).response;
      }

      const response = await this.post("SplitMonth", params);
      return response?.data || [];
    } catch (error) {
      console.error("Error in splitMonth:", error);
      throw error;
    }
  }

  public static async unsplitMonth(
    params: IMonthSplitParams,
  ): Promise<IMonthSplitResponse[]> {
    try {
      if (this.useMockUnsplitMonth) {
        return (
          unsplitMonthSampleData as IMockEnvelope<
            IMonthSplitParams,
            IMonthSplitResponse[]
          >
        ).response;
      }

      const response = await this.post("UnsplitMonth", params);
      return response?.data || [];
    } catch (error) {
      console.error("Error in unsplitMonth:", error);
      throw error;
    }
  }

  public static async createIndications(params: {
    contactName: string;
    party: string;
    marketer: string;

    indications: IIndication[];
  }): Promise<any[]> {
    console.info("createIndication", params);
    const response: any = await this.post(`IndicationCreate`, params);

    return response?.data || [];
  }

  public static async getIndicationHistory(params: {
    mode?: string;
    product: string;
    locationId: string;
    shipDate: string;
  }): Promise<IIndicationHistory[]> {
    const result = await this.proxy("IndicationHistory", params, 1, 20);
    console.log("IndicationHistory", result);
    return sortByField(result, "dateLogged", "DESC");
  }

  public static async updateOccupants(params: {
    id: string;
    occupants: IOccupant[];
  }): Promise<void> {
    await this.post(`/UpdateOccupantsAtLocation`, params);
  }

  public static async updateLocation(params: {
    id: string;
    name: string;
    owner: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    coordinates: [number, number];
    uom: string;
    railChanged?: boolean;
    railStationName?: string;
    equipmentTypes?: string[];
    carriers?: string[];
    railInbound?: boolean;
    railOutbound?: boolean;
  }) {
    const response: any = await this.post(`/UpdateLocation`, params);
    return response?.data || [];
  }

  // TODO: deprecate
  public static async updateLogistics(params: {
    id: string;
    equipmentType?: string;
    railStationName?: string;
    carriers?: string[];
    threeLegsAllowed?: boolean;
    modes?: {
      name: string;
      inbound: boolean;
      outbound: boolean;
    }[];
  }) {
    if (
      !params.equipmentType ||
      params.equipmentType === "" ||
      params.equipmentType === "undefined" ||
      params.equipmentType === "null"
    ) {
      // eslint-disable-next-line no-param-reassign
      delete params.equipmentType;
    }

    console.info("LogisticsUpdate", params);
    const response: any = await this.post(`/LogisticsUpdate`, params);

    return response?.data ?? [];
  }

  public static async updateMileageRequestStatus(
    params: IMileageRequestStatusParams,
  ): Promise<any[]> {
    try {
      const response: any = await this.post(`MileageRequest`, params);
      return response?.data || [];
    } catch (error) {
      console.error("MileageRequest update failed", error);
      throw error;
    }
  }

  public static async getRailStationsByZipCode(params: {
    zipCode: string;
  }): Promise<any[]> {
    const result = await this.proxy("RailStationsByZipCode", params, 1, 20);

    return result;
  }

  public static async getCarriersByRailStation(params: {
    stationState: string;
  }): Promise<ICarrier[]> {
    const result = await this.proxy("CarriersByRailStation", params, 1, 20);
    return result;
  }

  public static async getOccupantsByLocationId(params: { locationId: string }) {
    const result = await this.proxy("OccupantsByLocation", params, 1, 1);
    return result;
  }

  public static async getKochLocations(): Promise<IFavoriteOriginOption[]> {
    const result = await this.get("/proxy/KochLocations");
    return result?.data || [];
  }

  public static async getCompetitorLocations(): Promise<
    IFavoriteOriginOption[]
  > {
    const result = await this.get("/proxy/CompetitorLocations");
    return result?.data || [];
  }
}
